import * as React from 'react'
import { Primary } from '@sphinx/components-styles'
import { ChevronDownIcon, ChevronUpIcon } from '../icons'
import { Input } from '../input/input'
import { useClickOutside } from '../../hooks/useClickOutside'
import { NumberInputClass } from '@sphinx/components-styles'
import * as classNames from 'classnames'

const SPACE_BUTTON = 32

interface NumberInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  onChangeValue?: (number: number) => void
}

export const NumberInput: React.FC<NumberInputProps> = ({
  value: initialValue = 0,
  step = 1,
  max = Number.MAX_SAFE_INTEGER,
  min = Number.MIN_SAFE_INTEGER,
  disabled,
  onChangeValue,
  className,
  ...props
}) => {
  const changeNumber = (value: number) => Math.max(+min, Math.min(+max, value))

  const [select, setSelect] = React.useState(false)
  const [number, setNumber] = React.useState(changeNumber(+initialValue))
  const ref = React.useRef(null)

  useClickOutside(ref, () => setSelect(false))

  const digits = step.toString().includes('.')
    ? step.toString().split('.').pop().length
    : 0

  const toNumber = (value: number) =>
    changeNumber(Number(value.toFixed(digits)))

  const setNewValue = (newValue: number) => {
    setNumber(newValue)
    if (onChangeValue) {
      onChangeValue(newValue)
    }
  }

  const onChangeInput = ({
    target: { value },
  }: React.ChangeEvent<HTMLInputElement>) => setNewValue(toNumber(+value))

  return (
    <div className={classNames(NumberInputClass.wrap, className)} ref={ref}>
      <div className={NumberInputClass.numberHandlers}>
        <button
          className={NumberInputClass.handler}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            setNewValue(toNumber(number + +step))
            if (!select) {
              setSelect(true)
            }
          }}
          onKeyDown={(e) => {
            if (e.keyCode === SPACE_BUTTON) {
              setNewValue(toNumber(number + +step))
            }
          }}
          disabled={disabled || number === max}
        >
          <ChevronUpIcon />
        </button>
        <button
          className={NumberInputClass.handler}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            setNewValue(toNumber(number - +step))
            if (!select) {
              setSelect(true)
            }
          }}
          onKeyDown={(e) => {
            if (e.keyCode === SPACE_BUTTON) {
              setNewValue(toNumber(number - +step))
            }
          }}
          disabled={disabled || number === min}
        >
          <ChevronDownIcon />
        </button>
      </div>
      <Input
        {...props}
        style={{
          ...props.style,
          border: select && `1px solid ${Primary.orange[100]}`,
        }}
        type="number"
        step={step}
        max={max}
        min={min}
        value={number.toString()}
        onChange={onChangeInput}
        disabled={disabled}
      />
    </div>
  )
}
